import { useMemo } from "react";
import useSWR from "swr";

import {
  ApiCompetition,
  ApiCompetitionEntry,
  ApiCompetitionEntryInline,
  ApiCompetitionJudge,
  ApiInlineUser,
  ApiLeague,
  ApiLeagueScorecard,
  ApiRouteAttempt,
} from "../types";
import useVenueRoutes from "./useVenueRoutes";

export default function useCompetition(uuid?: string) {
  const {
    data: competition,
    isValidating,
    error,
    mutate,
  } = useSWR<ApiCompetition>(uuid && `/competitions/${uuid}`);
  const revalidate = () => mutate();
  return {
    competition,
    venue: competition?.venues[0],
    isValidating,
    error,
    revalidate,
  };
}

export function useCompetitionEntries(uuid?: string) {
  const { competition } = useCompetition(uuid);
  const {
    data: entries,
    isValidating,
    error,
  } = useSWR<ApiCompetitionEntryInline[]>(
    uuid && `/competitions/${uuid}/entries`
  );

  const mappedEntries = useMemo(() => {
    return entries?.map((entry) => ({
      ...entry,
      category: competition?.stages?.[0]?.categories.find(
        (cat) => cat.uuid === entry.category
      ),
    }));
  }, [competition, entries]);

  return { entries: mappedEntries, isValidating, error };
}

export function useCompetitionEntry({
  userUUID,
  competitionUUID,
}: {
  userUUID?: string;
  competitionUUID?: string;
}) {
  const { entries, isValidating, error } =
    useCompetitionEntries(competitionUUID);

  return {
    entry: entries?.find((x) => x.user.uuid === userUUID),
    isValidating,
    error,
  };
}

export function useCompetitionScorecard({
  userUUID,
  competitionUUID,
}: {
  userUUID?: string;
  competitionUUID?: string;
}) {
  const {
    data: entry,
    isValidating,
    error,
  } = useSWR<ApiCompetitionEntry>(
    `/competitions/${competitionUUID}/entries/${userUUID}`
  );

  return {
    entry,
    isValidating,
    error,
  };
}

export function useCompetitionRoutes({
  venueUUID,
  competitionUUID,
}: {
  venueUUID?: string;
  competitionUUID?: string;
}) {
  const { routes, isValidating, error } = useVenueRoutes(venueUUID);
  return {
    routes: routes?.filter((route) =>
      route.competitionRouteGroups?.some(
        (crg) => crg.competition === competitionUUID
      )
    ),
    isValidating,
    error,
  };
}

export function useCompetitionRoutesForJudge({
  competition,
  venueUUID,
  judgeUUID,
}: {
  competition: ApiCompetition | undefined;
  venueUUID: string | undefined;
  judgeUUID: string | undefined;
}) {
  const { routes, isValidating, error } = useCompetitionRoutes({
    venueUUID,
    competitionUUID: competition?.uuid,
  });
  const isMaster = competition.masterJudge?.uuid === judgeUUID;
  const isStaff = competition.role === "STAFF_JUDGE";

  let visibleRoutes, judgeAllowedRoutes;

  const judgingGroups = competition?.judgingGroups?.filter((group) =>
    group.judges?.some((judge) => judge.uuid === judgeUUID)
  );
  const assignedRouteUUIDs = judgingGroups?.flatMap((group) => group.routes);

  const assignedRoutes = routes?.filter((route) =>
    assignedRouteUUIDs?.includes(route.uuid)
  );

  if (isMaster || isStaff) {
    visibleRoutes = judgeAllowedRoutes = routes;
  } else {
    switch (competition?.settings.judgeRoutePermissions) {
      case "SEE_JUDGE_ASSIGNED":
        visibleRoutes = judgeAllowedRoutes = assignedRoutes;
        break;
      case "SEE_ALL_JUDGE_ASSIGNED":
        visibleRoutes = routes;
        judgeAllowedRoutes = assignedRoutes;
        break;
      case "SEE_JUDGE_ALL":
        visibleRoutes = routes;
        judgeAllowedRoutes = routes;
        break;
      default:
        visibleRoutes = judgeAllowedRoutes = undefined;
    }
  }

  return {
    visibleRoutes,
    judgeAllowedRoutes,
    assignedRoutes,
    isValidating,
    error,
  };
}

export function useCompetitionRouteJudgePermissions({
  competition,
  routeUUID,
  judgeUUID,
}: {
  competition: ApiCompetition | undefined;
  routeUUID: string | undefined;
  judgeUUID: string | undefined;
}) {
  return useMemo(() => {
    if (!competition || !routeUUID || !judgeUUID) {
      return undefined;
    }
    const isMaster = competition.masterJudge?.uuid === judgeUUID;
    const isStaff = competition.role === "STAFF_JUDGE";
    if (isMaster || isStaff) {
      return { canJudge: true, canView: true };
    }
    const judgingGroups = competition?.judgingGroups?.filter((group) =>
      group.judges?.some((judge) => judge.uuid === judgeUUID)
    );
    const assignedRouteUUIDs = judgingGroups?.flatMap((group) => group.routes);
    const isAssignedToRoute = assignedRouteUUIDs?.includes(routeUUID);

    const canJudge =
      isAssignedToRoute ||
      competition.settings.judgeRoutePermissions === "SEE_JUDGE_ALL";
    const canView =
      competition.settings.judgeRoutePermissions === "SEE_JUDGE_ALL" ||
      competition.settings.judgeRoutePermissions === "SEE_ALL_JUDGE_ASSIGNED" ||
      isAssignedToRoute;
    return { canJudge, canView };
  }, [competition, routeUUID, judgeUUID]);
}

export function useCompetitionRouteAttemptsJudgePermissions({
  competition,
  routeUUID,
  judgeUUID,
  attempts,
}: {
  competition: ApiCompetition | undefined;
  routeUUID: string | undefined;
  judgeUUID: string | undefined;
  attempts: { judge?: string | null }[] | undefined;
}) {
  const routePermissions = useCompetitionRouteJudgePermissions({
    competition,
    routeUUID,
    judgeUUID,
  });
  if (
    !competition ||
    !routeUUID ||
    !judgeUUID ||
    !routePermissions ||
    !attempts
  ) {
    return undefined;
  }

  const isMaster = competition.masterJudge?.uuid === judgeUUID;
  const isStaff = competition.role === "STAFF_JUDGE";
  if (isMaster || isStaff) {
    return {
      canView: true,
      canClear: true,
      canModify: true,
      canAdd: true,
    };
  }
  if (competition.settings.judgeAttemptModificationPermissions === "ALL") {
    return {
      canView: routePermissions.canView,
      canClear: routePermissions.canJudge,
      canModify: routePermissions.canJudge,
      canAdd: routePermissions.canJudge,
    };
  }
  if (competition.settings.judgeAttemptModificationPermissions === "NONE") {
    return {
      canClear: false,
      canModify: false,
      canAdd: routePermissions.canJudge,
      canView: routePermissions.canView,
    };
  }
  if (competition.settings.judgeAttemptModificationPermissions === "OWN") {
    const hasOtherJudgeAttempts = attempts?.some(
      (attempt) => attempt.judge !== judgeUUID
    );
    const canClear = routePermissions.canJudge && !hasOtherJudgeAttempts;
    const canModify = routePermissions.canJudge && !hasOtherJudgeAttempts;
    const canAdd = routePermissions.canJudge;
    return { canClear, canModify, canAdd, canView: routePermissions.canView };
  }

  // Should never happen!
  return undefined;
}

export function useCompetitionJudgesUUIDsToUsers(
  competition: ApiCompetition | undefined
) {
  return useMemo(() => {
    const usersMap: Record<string, ApiInlineUser> = {};
    competition?.judgingGroups.forEach((group) => {
      group.judges.forEach((judge) => {
        usersMap[judge.uuid] = judge;
      });
    });
    return usersMap;
  }, [competition?.judgingGroups]);
}

export function useCompetitionJudges(competitionUUID?: string) {
  const {
    data: judges,
    isValidating,
    error,
  } = useSWR<ApiCompetitionJudge[]>(`/competitions/${competitionUUID}/judges`);
  return { judges, isValidating, error };
}

export function useLeague(leagueUUID?: string) {
  const {
    data: league,
    isValidating,
    error,
  } = useSWR<ApiLeague>(`/leagues/${leagueUUID}`);
  return { league, isValidating, error };
}

type UseLeagueEntryParams = {
  leagueUUID?: string;
  userUUID?: string;
};

export function useLeagueEntry({ leagueUUID, userUUID }: UseLeagueEntryParams) {
  const {
    data: entry,
    isValidating,
    error,
  } = useSWR<ApiLeagueScorecard>(
    leagueUUID && userUUID
      ? `/leagues/${leagueUUID}/leaderboard/${userUUID}`
      : undefined
  );
  return { entry, isValidating, error };
}
